// admin/program/components/PublishProgram.tsx
import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { Timestamp } from 'firebase/firestore';
import { programService, Program } from '../../../firebase/programFirestoreService';
import { useUI } from '../../../components/common/UIMessageProvider';

const PublishProgram: React.FC = () => {
  const auth = useAuth();
  const canPublishPrograms = auth.canPerformAction('programs', 'publish') || auth.canPerformAction('programs', 'edit');
  const [programs, setPrograms] = useState<Program[]>([]);
  const [selectedProgramId, setSelectedProgramId] = useState<string>('');
  const [registrationStart, setRegistrationStart] = useState<string>('');
  const [registrationEnd, setRegistrationEnd] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const ui = useUI();

  useEffect(() => {
    loadUnpublishedPrograms();
  }, []);

  const loadUnpublishedPrograms = async () => {
    try {
      const allPrograms = await programService.getPrograms();
      const unpublished = allPrograms.filter(p => !p.isPublished);
      setPrograms(unpublished);
    } catch (error) {
      console.error('Error loading programs:', error);
    }
  };

  const handlePublish = async () => {
    if (!selectedProgramId || !registrationStart || !registrationEnd) {
      ui.showMessage('Please select a program and set registration dates', { type: 'warning' });
      return;
    }

    if (new Date(registrationStart) >= new Date(registrationEnd)) {
      ui.showMessage('Registration end date must be after start date', { type: 'warning' });
      return;
    }

    setLoading(true);
    try {
      await programService.publishProgram(selectedProgramId, {
        registrationStart: Timestamp.fromDate(new Date(registrationStart)),
        registrationEnd: Timestamp.fromDate(new Date(registrationEnd))
      });
      ui.showMessage('Program published successfully!', { type: 'success' });
      setSelectedProgramId('');
      setRegistrationStart('');
      setRegistrationEnd('');
      loadUnpublishedPrograms();
    } catch (error) {
      console.error('Error publishing program:', error);
      ui.showMessage('Failed to publish program', { type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const selectedProgram = programs.find(p => p.id === selectedProgramId);

  return (
    <div className="bg-white p-4 rounded shadow">
      <h2 className="text-base font-semibold text-gray-800 mb-3">Publish Program</h2>
      
      <div className="space-y-3">
        {/* Program select + Registration Dates on one row */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Select Program to Publish *
            </label>
            <select
              value={selectedProgramId}
              onChange={(e) => setSelectedProgramId(e.target.value)}
              className="w-full px-2 py-1 border border-gray-300 rounded-md"
            >
              <option value="">Choose a program</option>
              {programs.map(program => (
                <option key={program.id} value={program.id}>
                  {program.title} ({program.startDate?.toDate().toLocaleDateString()})
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Registration Start Date *
            </label>
            <input
              type="datetime-local"
              value={registrationStart}
              onChange={(e) => setRegistrationStart(e.target.value)}
              className="w-full px-2 py-1 border border-gray-300 rounded-md"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Registration End Date *
            </label>
            <input
              type="datetime-local"
              value={registrationEnd}
              onChange={(e) => setRegistrationEnd(e.target.value)}
              className="w-full px-2 py-1 border border-gray-300 rounded-md"
            />
          </div>
        </div>

        {selectedProgram && (
          <div className="bg-gray-50 p-4 rounded-md">
            <h3 className="font-medium text-gray-800">{selectedProgram.title}</h3>
            <p className="text-sm text-gray-600 mt-1">{selectedProgram.description}</p>
            <div className="grid grid-cols-2 gap-4 mt-3 text-sm">
              <div>
                <span className="text-gray-500">Program Dates:</span>
                <p className="font-medium">
                  {selectedProgram.startDate?.toDate().toLocaleDateString()} - 
                  {selectedProgram.endDate?.toDate().toLocaleDateString()}
                </p>
              </div>
              <div>
                <span className="text-gray-500">Location:</span>
                <p className="font-medium">{selectedProgram.location}</p>
              </div>
            </div>
          </div>
        )}

        {/* Publish Button */}
        <div className="flex justify-end pt-3">
          {canPublishPrograms && (
            <button
              onClick={handlePublish}
              disabled={loading}
              className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 text-sm"
            >
              {loading ? 'Publishing...' : 'Publish'}
            </button>
          )}
          {!canPublishPrograms && (
            <div className="text-sm text-gray-500">You do not have permission to publish programs.</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PublishProgram;